import { NavigationExtras, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { MediaCategory, myListPageSlug } from '../../public-api';

/**
 * Represents a single navigation to an internal route
 */
export interface NavigationEntry {
  /** The url of the navigation. Use undefined for same page navigation. */
  url?: string;
  /** The navigation extras for navigation, like queryParams and skipLocationChange. */
  extras?: NavigationExtras;
}

/* eslint-disable max-len */
// prettier-ignore
export class NavigationOptions {
  public readonly home = () => '/';
  public readonly geolocationBlock = () => '/geolocation-block';
  public readonly selectProfile = () => '/select-profile';
  public readonly notFound = (skip: boolean = true) => ({ url: '/not-found', extras: { skipLocationChange: skip } } as NavigationEntry);
  public readonly page = (pageSlug: string) => `/${pageSlug}`;
  public readonly listView = (pageSlug: string, pageItemSlug: string) => `/category/${pageSlug}/${pageItemSlug}`;
  public readonly myList = () => `/${myListPageSlug}`;
  public readonly browse = (pageItemSlug: string) => `/${pageItemSlug}`;
  public readonly browseWithTitle = (pageSlug: string, pageItemSlug: string, titleSlug: string) =>
    `/category/${pageSlug}/${pageItemSlug}/${titleSlug}`;
  public readonly browseGenre = (genre: string) => `/genre/${genre}`;
  public readonly browseCollection = (collection: string) => `/collection/${collection}`;
  public readonly browseCast = (id: string, cast: string) => `/cast/${id}/${cast}`;
  public readonly browseDirector = (id: string, director: string) => `/director/${id}/${director}`;
  public readonly browseRecommended = (id: string) => `/recommended/${id}`;
  public readonly search = () => `/search`;
  public readonly searchWithQuery = (queryParams: Record<string, string>) => ({
    url: undefined,
    extras: { queryParams, replaceUrl: true },
  });
  public readonly watch = (guid: string, isTrailer: boolean, fromPlayer?: boolean) => ({
    url: `/watch/`,
    extras: { queryParams: { guid, mediaCategory: isTrailer ? MediaCategory.PREVIEW : MediaCategory.VIDEO }, replaceUrl: fromPlayer },
  });
  public readonly movieDetails = (movieId: string) => `/movies/${movieId}`;
  public readonly seriesDetails = (seriesId: string) => `/series/${seriesId}`;
  public readonly seasonDetails = (seriesId: string, seasonId: string) => `/series/${seriesId}/${seasonId}`;
  public readonly episodeDetails = (seriesId: string, seasonId: string, episodeId: string) =>
    `/series/${seriesId}/${seasonId}/${episodeId}`;
  public readonly redeem = () => '/redeem';
  public readonly promotion = () => '/promotion';
}
/* eslint-enable max-len */

export abstract class AbstractNavigationService {
  protected readonly navigationOptions = new NavigationOptions();

  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
  public constructor(protected readonly _router: Router) {}

  protected abstract readonly languageAndCountry$: BehaviorSubject<string>;

  /**
   * Navigates to a selected navigation option.
   *
   * @param option The desired target of the navigation
   * @param args The additional arguments for navigation variables
   * @returns {Promise<boolean>} A promise that resolves when the navigation succeeded and rejects when failed
   */
  public abstract navigate<Option extends keyof NavigationOptions, Args extends Parameters<NavigationOptions[Option]>>(
    option: Option,
    ...args: Args
  ): Promise<boolean>;

  /**
   * Gets the prepared URL for a selected navigation option.
   *
   * @param option The desired navigation option
   * @param args The additional arguments for navigation variables
   * @returns {string} The prepared url with variables filled in
   */
  public abstract getUrl<Option extends keyof NavigationOptions, Args extends Parameters<NavigationOptions[Option]>>(
    option: Option,
    ...args: Args
  ): string;
}
